import React from 'react';
import { ArrowRight } from 'lucide-react';
import heroPortrait from '@/assets/hero-portrait.png';
import { useNavigate } from 'react-router-dom';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();

  const handleDiagnostic = () => navigate('/diagnostic');

  const handleDiscover = () => {
    document.getElementById('methode')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center section-lg hero-grain overflow-hidden pt-20">

      {/* ── Atmospheric orbs ────────────────────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full animate-float"
          style={{
            top: '-8%',
            right: '-12%',
            width: '680px',
            height: '680px',
            background: 'hsl(var(--rose) / 0.07)',
            filter: 'blur(110px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: '2%',
            left: '-10%',
            width: '560px',
            height: '560px',
            background: 'hsl(var(--lilas) / 0.055)',
            filter: 'blur(100px)',
            animationDelay: '3.5s',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: '20%',
            right: '5%',
            width: '220px',
            height: '220px',
            background: 'hsl(var(--rose) / 0.04)',
            filter: 'blur(60px)',
          }}
        />
      </div>

      {/* ── Content — Split layout ───────────────────────── */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-12 max-w-6xl mx-auto">

          {/* ── Pill overline — mobile only, above image ── */}
          <div className="reveal md:hidden text-center w-full order-first mb-0">
            <span className="reveal-child badge-pill inline-flex">
              <span className="badge-pill-dot" />
              Stratégie · Tunnels · Automatisations
            </span>
          </div>

          {/* ── Image column (mobile: second, desktop: last) ── */}
          <div className="reveal w-full md:w-[42%] order-2 md:order-last flex justify-center">
            <div
              className="reveal-child relative w-full max-w-xs md:max-w-none shadow-glow min-h-[280px]"
              style={{
                borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                overflow: 'hidden',
              }}
            >
              <div className="aspect-[4/5] md:aspect-[3/4]">
                <img
                  src={heroPortrait}
                  alt="Tina — Glow Lab, stratégie digitale"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
              </div>
              {/* Rose glow overlay — subtle */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--rose) / 0.08), transparent 60%)',
                }}
              />
            </div>
          </div>

          {/* ── Text column ──────────────────────────────── */}
          <div className="w-full md:w-[58%] text-center md:text-left order-3 md:order-first">

            {/* Pill overline — desktop only */}
            <div className="reveal mb-10 hidden md:block">
              <span className="reveal-child badge-pill inline-flex">
                <span className="badge-pill-dot" />
                Stratégie · Tunnels · Automatisations
              </span>
            </div>

            {/* H1 */}
            <div className="reveal">
              <h1
                className="reveal-child font-display text-foreground mb-8"
                style={{ letterSpacing: '-0.015em' }}
              >
                <span
                  className="block font-semibold leading-[1.04]"
                  style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4.8rem)' }}
                >
                  Je construis le système digital
                </span>
                <span
                  className="block font-semibold leading-[1.04]"
                  style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4.8rem)' }}
                >
                  qui fait vendre ta marque
                </span>
                <span
                  className="block font-medium leading-[1.04]"
                  style={{
                    fontSize: 'clamp(2.2rem, 5.5vw, 4.8rem)',
                    fontStyle: 'italic',
                    color: 'hsl(var(--rose))',
                  }}
                >
                  — même quand tu dors.
                </span>
              </h1>

              {/* Decorative separator */}
              <div className="reveal-child flex items-center justify-center md:justify-start gap-3 mb-10">
                <div className="h-px bg-border w-10" />
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: 'hsl(var(--rose))' }}
                />
                <div className="h-px bg-border w-10" />
              </div>

              {/* Subtitle */}
              <p className="reveal-child text-lg lg:text-xl text-muted-foreground max-w-xl mx-auto md:mx-0 mb-4 leading-relaxed">
                Positionnement, tunnels de vente, séquences email, automatisations —{' '}
                <strong className="text-foreground font-medium">
                  je construis tout pour toi en 6 semaines.
                </strong>
              </p>
              <p className="reveal-child text-base text-muted-foreground max-w-xl mx-auto md:mx-0 mb-12 leading-relaxed">
                Tu n'as plus qu'à te concentrer sur ce que tu aimes : créer.
              </p>

              {/* CTAs */}
              <div className="reveal-child flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-4">
                <button
                  onClick={handleDiagnostic}
                  className="group bg-primary text-primary-foreground font-semibold text-sm px-8 py-4 rounded-xl transition-all duration-300 flex items-center gap-2.5 shadow-glow hover:scale-[1.02] hover:shadow-card-hover w-full sm:w-auto justify-center"
                >
                  Réserver mon Diagnostic — 590€
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={handleDiscover}
                  className="group text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 flex items-center gap-1.5 underline-offset-4 hover:underline"
                >
                  Découvrir la méthode
                  <span className="inline-block transition-transform duration-300 group-hover:translate-y-0.5">
                    ↓
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom page-blend fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, hsl(var(--background)), transparent)',
        }}
      />
    </section>
  );
};

export default HeroSection;
